import { axiosInstance } from '@src/services/common';
import type { FilterCondition, UserJourneyFilterRequest } from '@src/shared/types/user-journeys';
import type { PatternsRequest } from '@src/shared/types/patterns';

const API_BASE_URL = '/gm';

export function postStableVersion(customerId: number, data: any) {
  const { version } = data;
  return axiosInstance.post(`${API_BASE_URL}/fetch_files/flow_suggestion/${customerId}/stable`, {
    version,
  });
}

export function getDataByConditions() {
  return axiosInstance.post(`${API_BASE_URL}/cruncher/pattern-summary`, {});
}

export function getCriticalEvent(id: string) {
  return axiosInstance.get(`${API_BASE_URL}/v1/critical_events/${id}`);
}

export interface CreateCriticalEventRequest {
  name: string;
  rules: CriticalEventRule[][];
  is_milestone: boolean;
}

export interface UpdateCriticalEventRequest {
  name: string;
  rules: CriticalEventRule[][];
  is_milestone: boolean;
}

export interface CriticalEventResponse {
  critical_event: CriticalEvent;
  message: string;
}

export function createCriticalEvent(data: CreateCriticalEventRequest) {
  return axiosInstance.post<CriticalEventResponse>(`${API_BASE_URL}/v1/critical_events`, data);
}

export function updateCriticalEvent(ceId: string, data: UpdateCriticalEventRequest) {
  return axiosInstance.put<CriticalEventResponse>(
    `${API_BASE_URL}/v1/critical_events/${ceId}`,
    data,
  );
}

export interface CreateDimensionRequest {
  name: string;
  rules: DimensionRule[][];
}

export interface UpdateDimensionRequest {
  name: string;
  rules: DimensionRule[][];
}

export interface CreateDimensionResponse {
  cd_id: string;
}

export interface UpdateDimensionResponse {
  message: string;
}

export function getDimension(dimensionId: string) {
  return axiosInstance.get<Dimension>(`${API_BASE_URL}/v1/dimensions/${dimensionId}`);
}

export function createDimension(data: CreateDimensionRequest) {
  return axiosInstance.post<CreateDimensionResponse>(`${API_BASE_URL}/v1/dimensions`, data);
}

export function updateDimension(cdId: string, data: UpdateDimensionRequest) {
  return axiosInstance.put<UpdateDimensionResponse>(`${API_BASE_URL}/v1/dimensions/${cdId}`, data);
}

export function postUserJourneysFilter(data: UserJourneyFilterRequest) {
  return axiosInstance.post(`${API_BASE_URL}/v1/user_journeys/filter`, data);
}

export function getPatterns(data: PatternsRequest) {
  return axiosInstance.post(`${API_BASE_URL}/v1/patterns/list`, data);
}

export interface PatternQueryFilter {
  field: string;
  op: string;
  val: string;
  type: string;
}

export interface PatternQueryGroupBy {
  field: string;
  type: string;
}

export interface PatternQueryOrderBy {
  field: string;
  order: string;
}

export interface PatternQueryRequest {
  pattern: string[];
  time_range: [string, string];
  timeout_ms: number;
  filters?: FilterCondition[][];
  limit?: number;
  group_by?: PatternQueryGroupBy[];
  aggregations?: string[];
  order_by?: PatternQueryOrderBy[];
}

export function postPatternQuery(data: PatternQueryRequest) {
  return axiosInstance.post(`${API_BASE_URL}/v1/pattern/query`, data);
}

export interface PatternsQueryRequest {
  patterns: string[];
  time_range: [string, string];
  timeout_ms: number;
  filters?: PatternQueryFilter[][];
  limit?: number;
  group_by?: PatternQueryGroupBy[];
  aggregations?: string[];
  order_by?: PatternQueryOrderBy[];
}

export interface PatternsQueryResponse {
  [patternId: string]: Array<{
    pattern_name: string;
    step: string;
    count_of_records?: number;
    mean_time_since_prior_ms?: number;
    mean_time_since_beginning_ms?: number;
    avg_conversion_steps_from_beginning?: number;
    avg_conversion_steps?: number;
    unique_user_id?: number;
    unique_client_id?: number;
    conversion_rate?: number;
    user_conversion_rate?: number;
    device_conversion_rate?: number;
    event_name?: string;
    [key: string]: any;
  }>;
}

export function postPatternsQuery(data: PatternsQueryRequest) {
  return axiosInstance.post<PatternsQueryResponse>(`${API_BASE_URL}/v1/patterns/query`, data);
}

export interface MilestoneQueryRequest {
  milestone: string;
  time_range: [string, string];
  filters?: FilterCondition[][];
  limit?: number;
  group_by?: PatternQueryGroupBy[];
  aggregations?: string[];
  order_by?: PatternQueryOrderBy[];
}

export function postMilestoneQuery(data: MilestoneQueryRequest) {
  return axiosInstance.post(`${API_BASE_URL}/v1/milestone/query`, data);
}
